import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface Message {
  id: string;
  senderId: string;
  receiverId: string;
  content: string;
  contextType?: string;
  contextId?: string;
  status: 'SENT' | 'DELIVERED' | 'READ';
  createdAt: Date;
  deliveredAt?: Date;
  readAt?: Date;
  sender?: {
    id: string;
    firstName: string;
    lastName: string;
    profilePicture?: string;
  };
  receiver?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface TypingIndicator {
  userId: string;
  isTyping: boolean;
  contextId?: string;
  user?: {
    firstName: string;
    lastName: string;
  };
}

interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  isOnline: (userId: string) => boolean;
  sendMessage: (data: {
    receiverId: string;
    content: string;
    contextType?: string;
    contextId?: string;
  }) => Promise<{ 
    success: boolean; 
    message?: Message; 
    error?: string;
    rateLimitExceeded?: boolean;
    flagged?: boolean;
  }>;
  markAsDelivered: (messageIds: string[]) => void;
  markAsRead: (messageIds: string[]) => void;
  startTyping: (receiverId: string, contextId?: string) => void;
  stopTyping: (receiverId: string, contextId?: string) => void;
  onMessageReceive: (callback: (message: Message) => void) => () => void;
  onMessageStatusUpdate: (callback: (data: { messageId: string; status: string; deliveredAt?: Date; readAt?: Date }) => void) => () => void;
  onTypingIndicator: (callback: (data: TypingIndicator) => void) => () => void;
  onUserOnlineStatus: (callback: (data: { userId: string; isOnline: boolean }) => void) => () => void;
  checkOnlineStatus: (userIds: string[]) => Promise<Record<string, boolean>>;
}

const SocketContext = createContext<SocketContextType | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

interface SocketProviderProps {
  children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { token, user, refreshUserData } = useAuth();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const socketRef = useRef<Socket | null>(null);

  // Initialize Socket.IO connection
  useEffect(() => {
    if (!token || !user) {
      // Disconnect if no token
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Get backend URL - use same logic as apiService
    const BACKEND_URL = process.env.NODE_ENV === 'production'
      ? 'https://rideclubnet.com'
      : (process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:3001');

    console.log('🔌 Connecting to Socket.IO server:', BACKEND_URL);
    console.log('🔑 Token available:', token ? 'Yes (' + token.substring(0, 20) + '...)' : 'No');

    const newSocket = io(BACKEND_URL, {
      auth: {
        token
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });

    newSocket.on('connect', () => {
      console.log('✅ Socket.IO connected:', newSocket.id);
      setIsConnected(true);
    });

    newSocket.on('connected', (data) => {
      console.log('👤 User authenticated:', data.userId);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('❌ Socket.IO disconnected:', reason);
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('🚫 Socket.IO connection error:', error.message);
      setIsConnected(false);
    });

    // Handle user online status updates
    newSocket.on('user:online:status', ({ userId, isOnline }: { userId: string; isOnline: boolean }) => {
      setOnlineUsers(prev => {
        const newSet = new Set(prev);
        if (isOnline) {
          newSet.add(userId);
        } else {
          newSet.delete(userId);
        }
        return newSet;
      });
    });

    // Handle real-time user profile refresh
    newSocket.on('user:updated', () => {
      console.log('🔄 User profile update notification received via socket. Refreshing...');
      refreshUserData().catch((err: any) => console.error('Socket user refresh error:', err));
    });

    // Handle real-time driver status updates
    newSocket.on('driver-status-updated', (data: {
      driverStatus: string;
      isLicenseVerified: boolean;
      faceMatchScore?: number;
      role?: string;
    }) => {
      console.log('🚗 Driver status update received via socket:', data);
      refreshUserData().catch((err: any) => console.error('Socket driver status refresh error:', err));
    });

    socketRef.current = newSocket;
    setSocket(newSocket);

    return () => {
      console.log('🔌 Cleaning up Socket.IO connection');
      newSocket.disconnect();
      socketRef.current = null;
    };
  }, [token, user, refreshUserData]);

  // Check if a user is online
  const isOnline = useCallback((userId: string): boolean => {
    return onlineUsers.has(userId);
  }, [onlineUsers]);

  // Send a message
  const sendMessage = useCallback((data: {
    receiverId: string;
    content: string;
    contextType?: string;
    contextId?: string;
  }): Promise<{ success: boolean; message?: Message; error?: string }> => {
    return new Promise((resolve) => {
      if (!socketRef.current || !isConnected) {
        resolve({ success: false, error: 'Not connected to server' });
        return;
      }

      socketRef.current.emit('message:send', data, (response: any) => {
        resolve(response);
      });
    });
  }, [isConnected]);

  // Mark messages as delivered
  const markAsDelivered = useCallback((messageIds: string[]) => {
    if (!socketRef.current || !isConnected || messageIds.length === 0) return;

    socketRef.current.emit('message:delivered', messageIds, (response: any) => {
      console.log(`✓✓ ${response.count} messages marked as delivered`);
    });
  }, [isConnected]);

  // Mark messages as read
  const markAsRead = useCallback((messageIds: string[]) => {
    if (!socketRef.current || !isConnected || messageIds.length === 0) return;

    socketRef.current.emit('message:read', messageIds, (response: any) => {
      console.log(`✓✓ (green) ${response.count} messages marked as read`);
    });
  }, [isConnected]);

  // Start typing indicator
  const startTyping = useCallback((receiverId: string, contextId?: string) => {
    if (!socketRef.current || !isConnected) return;
    socketRef.current.emit('typing:start', { receiverId, contextId });
  }, [isConnected]);

  // Stop typing indicator
  const stopTyping = useCallback((receiverId: string, contextId?: string) => {
    if (!socketRef.current || !isConnected) return;
    socketRef.current.emit('typing:stop', { receiverId, contextId });
  }, [isConnected]);

  // Listen for incoming messages
  const onMessageReceive = useCallback((callback: (message: Message) => void) => {
    if (!socketRef.current) {
      console.warn('⚠️ Socket not available when registering message:receive listener');
      return () => {};
    }

    // Create a wrapper function to add logging
    const wrappedCallback = (message: Message) => {
      console.log('📨 Received message via socket:', message.id, message.content);
      callback(message);
    };

    console.log('📥 Registering message:receive listener');
    socketRef.current.on('message:receive', wrappedCallback);

    return () => {
      if (socketRef.current) {
        console.log('📥 Unregistering message:receive listener');
        socketRef.current.off('message:receive', wrappedCallback);
      }
    };
  }, [socket]);

  // Listen for message status updates
  const onMessageStatusUpdate = useCallback((callback: (data: { messageId: string; status: string; deliveredAt?: Date; readAt?: Date }) => void) => {
    if (!socketRef.current) return () => {};

    socketRef.current.on('message:status:update', callback);

    return () => {
      if (socketRef.current) {
        socketRef.current.off('message:status:update', callback);
      }
    };
  }, []);

  // Listen for typing indicators
  const onTypingIndicator = useCallback((callback: (data: TypingIndicator) => void) => {
    if (!socketRef.current) return () => {};

    socketRef.current.on('typing:indicator', callback);

    return () => {
      if (socketRef.current) {
        socketRef.current.off('typing:indicator', callback);
      }
    };
  }, []);

  // Listen for user online status changes
  const onUserOnlineStatus = useCallback((callback: (data: { userId: string; isOnline: boolean }) => void) => {
    if (!socketRef.current) return () => {};

    socketRef.current.on('user:online:status', callback);

    return () => {
      if (socketRef.current) {
        socketRef.current.off('user:online:status', callback);
      }
    };
  }, []);

  // Check online status of multiple users
  const checkOnlineStatus = useCallback((userIds: string[]): Promise<Record<string, boolean>> => {
    return new Promise((resolve) => {
      if (!socketRef.current || !isConnected) {
        resolve({});
        return;
      }

      socketRef.current.emit('check:online:status', userIds, (onlineStatus: Record<string, boolean>) => {
        resolve(onlineStatus);
      });
    });
  }, [isConnected]);

  const value: SocketContextType = {
    socket,
    isConnected,
    isOnline,
    sendMessage,
    markAsDelivered,
    markAsRead,
    startTyping,
    stopTyping,
    onMessageReceive,
    onMessageStatusUpdate,
    onTypingIndicator,
    onUserOnlineStatus,
    checkOnlineStatus
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};

